import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { platformClient } from "@homy/api-client";
import {
  PageContainer,
  PageHeader,
  Button,
  Input,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@homy/ui";
import {
  ScrollText,
  Search,
  Filter,
  RefreshCw,
  Code,
  Copy,
  Check,
  CheckCircle2,
  AlertCircle,
  Clock,
  User,
  Building,
  Terminal,
} from "lucide-react";

export function AuditLogsPage() {
  const [operatorFilter, setOperatorFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("ALL");
  const [targetOrgFilter, setTargetOrgFilter] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [copied, setCopied] = useState(false);

  const {
    data: auditData,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["platform-audit-logs", operatorFilter, actionFilter, targetOrgFilter],
    queryFn: async () => {
      const params = {
        limit: 100,
        skip: 0,
      };
      if (operatorFilter.trim()) params.operator_id = operatorFilter.trim();
      if (actionFilter !== "ALL") params.action = actionFilter;
      if (targetOrgFilter.trim()) params.target_organization_id = targetOrgFilter.trim();

      const res = await platformClient.get("/audit-logs", { params });
      return res.data;
    },
  });

  const handleCopyPayload = () => {
    if (selectedLog?.changesPayload) {
      navigator.clipboard.writeText(
        JSON.stringify(selectedLog.changesPayload, null, 2)
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const logs = auditData?.items || [];
  const totalLogs = auditData?.total ?? logs.length;

  return (
    <PageContainer className="max-w-7xl mx-auto space-y-6">
      <PageHeader
        title="Platform Audit Trail"
        description="Immutable, tamper-proof audit logs recording all administrative operations, session issuances, and tenant mutations."
        actions={
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      {/* Filter and Search Toolbar */}
      <div className="flex flex-col md:flex-row items-center gap-3 bg-card border border-border p-3 rounded-lg">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter by operator email or operator ID..."
            value={operatorFilter}
            onChange={(e) => setOperatorFilter(e.target.value)}
            className="pl-9 bg-input"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <div className="flex items-center gap-1.5">
            <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="h-10 rounded-md border border-border bg-input px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
            >
              <option value="ALL">All Actions</option>
              <option value="GENERATE_IMPERSONATION_TOKEN">GENERATE_IMPERSONATION_TOKEN</option>
              <option value="CREATE_ORGANIZATION">CREATE_ORGANIZATION</option>
              <option value="UPDATE_ORGANIZATION_STATUS">UPDATE_ORGANIZATION_STATUS</option>
              <option value="SUSPEND_ORGANIZATION">SUSPEND_ORGANIZATION</option>
              <option value="LOGIN">LOGIN</option>
            </select>
          </div>

          <Input
            placeholder="Target Org ID..."
            value={targetOrgFilter}
            onChange={(e) => setTargetOrgFilter(e.target.value)}
            className="w-40 bg-input"
          />
        </div>
      </div>

      {/* Logs Table */}
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            Loading platform audit records...
          </div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground space-y-2">
            <ScrollText className="h-8 w-8 mx-auto text-muted-foreground/50" />
            <p>No audit records found matching your filters.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[170px]">Timestamp</TableHead>
                <TableHead>Operator</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Target Org</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>IP Address</TableHead>
                <TableHead className="text-right">Status</TableHead>
                <TableHead className="text-right">Payload</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => {
                const isSuccess = log.statusCode >= 200 && log.statusCode < 300;
                return (
                  <TableRow key={log.id}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3 w-3 shrink-0 opacity-60" />
                        <span>
                          {log.createdAt
                            ? new Date(log.createdAt).toLocaleString(undefined, {
                                dateStyle: "short",
                                timeStyle: "medium",
                              })
                            : "—"}
                        </span>
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="font-medium text-xs text-foreground flex items-center gap-1.5">
                        <User className="h-3 w-3 text-muted-foreground shrink-0" />
                        <span>{log.operatorEmail}</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground font-mono pl-4">
                        {log.operatorId}
                      </div>
                    </TableCell>

                    <TableCell>
                      <Badge
                        variant="secondary"
                        className="font-mono text-[10px] uppercase font-semibold"
                      >
                        {log.action}
                      </Badge>
                    </TableCell>

                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {log.targetOrganizationId ? (
                        <div className="flex items-center gap-1">
                          <Building className="h-3 w-3 shrink-0" />
                          <span className="truncate max-w-[120px]" title={log.targetOrganizationId}>
                            {log.targetOrganizationId.slice(0, 8)}...
                          </span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground/60">—</span>
                      )}
                    </TableCell>

                    <TableCell className="text-xs">
                      <span className="font-medium text-foreground">{log.resourceType || "—"}</span>
                      {log.resourceId && (
                        <span className="text-muted-foreground text-[10px] block font-mono">
                          {log.resourceId.slice(0, 8)}...
                        </span>
                      )}
                    </TableCell>

                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {log.ipAddress || "127.0.0.1"}
                    </TableCell>

                    <TableCell className="text-right">
                      <Badge
                        variant="outline"
                        className={
                          isSuccess
                            ? "border-emerald-500/30 text-emerald-400"
                            : "border-destructive/30 text-destructive"
                        }
                      >
                        {isSuccess ? (
                          <span className="flex items-center gap-1">
                            <CheckCircle2 className="h-2.5 w-2.5" />
                            {log.statusCode || 200}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <AlertCircle className="h-2.5 w-2.5" />
                            {log.statusCode || 500}
                          </span>
                        )}
                      </Badge>
                    </TableCell>

                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 gap-1 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => setSelectedLog(log)}
                      >
                        <Code className="h-3.5 w-3.5" />
                        Diff
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="text-xs text-muted-foreground flex justify-between items-center px-1">
        <span>Showing {logs.length} of {totalLogs} recorded platform events</span>
        <span className="font-mono text-[11px]">Storage: core.platform_audit_logs</span>
      </div>

      {/* JSON Diff & Payload Inspector Dialog */}
      <Dialog
        open={Boolean(selectedLog)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedLog(null);
            setCopied(false);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center gap-2 text-primary">
              <Terminal className="h-5 w-5" />
              <DialogTitle>Audit Event Payload & Diff Inspector</DialogTitle>
            </div>
            <DialogDescription>
              Detailed telemetry snapshot and state mutation changes captured by PlatformAuditMiddleware.
            </DialogDescription>
          </DialogHeader>

          {selectedLog && (
            <div className="space-y-4 py-2 flex-1 overflow-y-auto">
              {/* Event Metadata Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="rounded border border-border bg-muted/20 p-2.5">
                  <span className="text-muted-foreground text-[10px] block uppercase font-mono">
                    Action
                  </span>
                  <span className="font-semibold text-foreground">{selectedLog.action}</span>
                </div>
                <div className="rounded border border-border bg-muted/20 p-2.5">
                  <span className="text-muted-foreground text-[10px] block uppercase font-mono">
                    Status
                  </span>
                  <span className="font-semibold text-emerald-400">{selectedLog.statusCode || 200} OK</span>
                </div>
                <div className="rounded border border-border bg-muted/20 p-2.5">
                  <span className="text-muted-foreground text-[10px] block uppercase font-mono">
                    Operator
                  </span>
                  <span className="font-semibold text-foreground truncate block" title={selectedLog.operatorEmail}>
                    {selectedLog.operatorEmail}
                  </span>
                </div>
                <div className="rounded border border-border bg-muted/20 p-2.5">
                  <span className="text-muted-foreground text-[10px] block uppercase font-mono">
                    IP Address
                  </span>
                  <span className="font-mono text-foreground">{selectedLog.ipAddress || "127.0.0.1"}</span>
                </div>
              </div>

              {/* JSON Payload Viewer */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-foreground font-mono">
                    changes_payload (JSONB)
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    onClick={handleCopyPayload}
                  >
                    {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? "Copied" : "Copy JSON"}
                  </Button>
                </div>

                <pre className="rounded-lg border border-border bg-black/50 p-4 font-mono text-xs text-emerald-400 overflow-x-auto max-h-[300px]">
                  {selectedLog.changesPayload
                    ? JSON.stringify(selectedLog.changesPayload, null, 2)
                    : "{\n  \"message\": \"No payload modifications recorded for this event\"\n}"}
                </pre>
              </div>

              {selectedLog.userAgent && (
                <div className="space-y-1 text-xs">
                  <span className="text-muted-foreground font-mono text-[10px] block uppercase">
                    User Agent
                  </span>
                  <p className="text-muted-foreground font-mono text-[11px] bg-muted/20 p-2 rounded border border-border break-all">
                    {selectedLog.userAgent}
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setSelectedLog(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
